import type { ApiErrorBody, HealthResponse, ResourceName, ResourceResponse } from '@jarvis/shared';
import { CLIENT_HEADER } from '@jarvis/shared/constants';
import type { StateMode } from './prefs';

/* Vazio = mesma origem (em desenvolvimento, o Vite repassa /api para a API local).
   Em produção com API em outro domínio, defina VITE_API_BASE_URL no build. */
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const UNREACHABLE = 'Não foi possível falar com o servidor do Jarvis. Ele pode estar desligado.';

export class ApiError extends Error {
  constructor(message: string, readonly code: string, readonly requestId?: string, readonly fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers[CLIENT_HEADER] = 'web';
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  /* Teto de tempo: sem isto, uma chamada presa deixa a tela parada para sempre. */
  const timeout = AbortSignal.timeout(25_000);
  const sig = signal && typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, timeout]) : (signal ?? timeout);

  let res: Response;
  try {
    res = await fetch(BASE + path, { method, signal: sig, credentials: 'include', headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (err) {
    if (signal?.aborted) throw err;
    if (timeout.aborted) {
      throw new ApiError('O servidor do Jarvis demorou demais a responder. No plano gratuito ele hiberna; espere um minuto e tente de novo.', 'TIMEOUT');
    }
    throw new ApiError(UNREACHABLE, 'API_UNREACHABLE');
  }
  if (res.status === 204) return undefined as T;
  const parsed: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const e = parsed as ApiErrorBody | null;
    if (!e?.error && [502, 503, 504].includes(res.status)) throw new ApiError(UNREACHABLE, 'API_UNREACHABLE');
    /* Sessão expirou ou falta o segundo fator: o porteiro do app volta para a tela de login. */
    if (res.status === 401 && !path.startsWith('/api/auth/')) window.dispatchEvent(new Event('jm:auth-required'));
    throw new ApiError(e?.error?.message ?? `O servidor respondeu com erro ${res.status}.`, e?.error?.code ?? `HTTP_${res.status}`, e?.requestId, e?.error?.fields);
  }
  /* Resposta que não é da API (por exemplo, uma página HTML no lugar): tratar como servidor fora. */
  if (parsed === null) throw new ApiError(UNREACHABLE, 'BAD_RESPONSE');
  return parsed as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T = void>(path: string) => request<T>('DELETE', path)
};

function wait(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(t); reject(signal.reason); }, { once: true });
  });
}

/* Os modos "carregando" e "erro" do Laboratório de estados são simulados aqui, sem chamar a API.
   "demo" e "real" vão de fato ao servidor. */
export async function simulate(mode: StateMode, signal: AbortSignal): Promise<void> {
  if (mode === 'loading') return new Promise(() => {});
  if (mode === 'error') {
    await wait(450, signal);
    throw new ApiError('Falha simulada ao carregar este conteúdo. Nenhum serviço externo foi contatado.', 'SIMULATED_ERROR');
  }
}

export async function fetchResource<K extends ResourceName>(name: K, mode: StateMode, signal: AbortSignal): Promise<ResourceResponse<K>> {
  await simulate(mode, signal);
  return api.get<ResourceResponse<K>>(`/api/resources/${name}?mode=${mode === 'demo' ? 'demo' : 'real'}`, signal);
}

export function fetchHealth(signal?: AbortSignal) {
  return api.get<HealthResponse>('/api/health', signal);
}

/* Baixa a exportação como arquivo, sem abrir nova aba. */
export async function downloadExport() {
  const res = await fetch(`${BASE}/api/account/export`, { credentials: 'include', headers: { Accept: 'application/json' } }).catch(() => null);
  if (!res) throw new ApiError(UNREACHABLE, 'API_UNREACHABLE');
  if (!res.ok) throw new ApiError(`Não foi possível exportar (erro ${res.status}).`, `HTTP_${res.status}`);
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'jarvis-master-dados.json';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}
